import { RRule } from 'rrule';
import { EventOccurrences, FullEventData, ListEventsWithFullDataResponse } from '.';

import { getRRuleConfiguration } from '@carlito/date-utils';

export { getRRuleConfiguration } from '@carlito/date-utils';

export function findOccurrenceForDate(targetDate: Date, event: Pick<FullEventData, 'occurrences'>) {
  for (const occurrence of event.occurrences) {
    const startDateTime = new Date(occurrence.startDateTime);

    if (
      startDateTime.getDate() === targetDate.getDate() &&
      startDateTime.getMonth() === targetDate.getMonth() &&
      startDateTime.getFullYear() === targetDate.getFullYear()
    ) {
      return occurrence;
    }

    if (occurrence.repetition) {
      const rruleConfig = getRRuleConfiguration(
        {
          ...occurrence.repetition,
          repeatUntil: occurrence.repetition.repeatUntil
            ? new Date(occurrence.repetition.repeatUntil)
            : undefined,
        },
        {
          dtStart: startDateTime,
        }
      );

      const rule = new RRule(rruleConfig);

      const dates = rule.between(
        new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate()),
        new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59),
        true
      );

      if (dates.length > 0) {
        return occurrence;
      }
    }
  }
  return null;
}

type ListEventResponseDTO = ListEventsWithFullDataResponse['events'][0];

type FindOccurrencesForDateParams = {
  targetDate: Date;
  event: Pick<ListEventResponseDTO, 'occurrences'>;
};

export function findOccurrencesForDate({ targetDate, event }: FindOccurrencesForDateParams) {
  const validOccurrences = [];
  for (const occurrence of event.occurrences) {
    const startDateTime = new Date(occurrence.startDateTime);

    if (
      startDateTime.getDate() === targetDate.getDate() &&
      startDateTime.getMonth() === targetDate.getMonth() &&
      startDateTime.getFullYear() === targetDate.getFullYear()
    ) {
      validOccurrences.push(occurrence);
    }

    if (occurrence.repetition) {
      const rruleConfig = getRRuleConfiguration(
        {
          ...occurrence.repetition,
          repeatUntil: occurrence.repetition.repeatUntil
            ? new Date(occurrence.repetition.repeatUntil)
            : undefined,
        },
        {
          dtStart: startDateTime,
        }
      );

      const rule = new RRule(rruleConfig);

      const dates = rule.between(
        new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate()),
        new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59),
        true
      );

      if (dates.length > 0) {
        validOccurrences.push(occurrence);
      }
    }
  }

  // To remove duplicate occurrences, if any
  const resultantOccurrences = validOccurrences.filter(
    (slot, index, array) =>
      array.findIndex((el) => el.startDateTime === slot.startDateTime) === index
  );

  return resultantOccurrences;
}

export function dateToHHMM(date: Date) {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
}

export function durationToInputParsable(durationMs: number, eventStartDateTime: Date) {
  const endDateTime = new Date(eventStartDateTime.getTime() + durationMs);
  return dateToHHMM(endDateTime);
}

export type Occurrence = { startDateTime: string; durationMs: number };

export function isOccurrenceAllDay(occurrence: EventOccurrences[0]) {
  const start = new Date(occurrence.startDateTime);

  return (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    occurrence.durationMs === 24 * 60 * 60 * 1000
  );
}

export function convertOccurrenceTimeframesToReadableForm(occurrences: EventOccurrences) {
  return occurrences
    .map((occurrence) => {
      const startDateTime = new Date(occurrence.startDateTime);
      const startTime = dateToHHMM(startDateTime);
      const endTime = durationToInputParsable(occurrence.durationMs, startDateTime);

      const isAllDayEvent = isOccurrenceAllDay(occurrence);

      return {
        startTime,
        endTime,
        isAllDayEvent,
      };
    })
    .sort((a, b) => parseInt(a.startTime.split(':')[0]) - parseInt(b.startTime.split(':')[0]));
}

export const isEventBookableInAdvance = (params: {
  onDate: Date;
  daysBookableInAdvance?: number | null;
}) => {
  if (params.daysBookableInAdvance === null || params.daysBookableInAdvance === undefined) {
    return true;
  }
  const timeDifferenceInDays =
    (params.onDate.getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000);
  return timeDifferenceInDays <= params.daysBookableInAdvance;
};
